// Redemption Partners — 1-pager.
//
// Surfaces the in-app equivalent of
//   strategy/1 pager_Bvndle Merchant LaaS Update Report.docx (2 Jun 2026)
// with three differences from the source PDF:
//   1. Headline + category numbers are live-sensitive — the per-category
//      coin totals come from the Phase-3 Metabase query
//      `live-spend-share-by-category` when the live tab is healthy. If the
//      live tab is loading / unavailable, the snapshot in
//      `partners-current.ts` (sourced from the 2 Jun .docx) is used and a
//      muted "snapshot" chip is rendered.
//   2. The category table + merchant table are computed from
//      `partners-current.ts` so any merchant added to the master list
//      flows through here without copy-editing.
//   3. "Download as Word" emits a .docx file mirroring the layout, which
//      the user can open in Word / Google Docs and edit further.
//
// Structure (matches source 1-pager):
//   ┌ Hero header: Title + date
//   ├ Four KPI tiles: Merchants Targeted · Coins Redeemed YTD ·
//   │                  Priority Merchants · Merchant Categories
//   ├ Overview: two synthesised paragraphs
//   ├ 01 · Categories: 11-row table (label, description, onboarded#, %)
//   ├ 02 · Selection criteria: 6 rows
//   └ 03 · Merchant redemption: monthly + YTD table
//
// To regenerate the source docx (off-app): the Word download here is the
// canonical export path; teams should edit the downloaded .docx, not the
// in-app surface.

import { saveAs } from "file-saver";
import { useMemo } from "react";

import { useLiveCategoryStats } from "../lib/live-category-stats";
import {
  HEADLINE,
  MERCHANTS,
  PARTNER_CATEGORIES,
  rollupAll,
  type Merchant,
} from "../lib/partners-current";
import { buildOnePagerDocx } from "../lib/one-pager-docx";

const SOURCE_DATE = "2 June 2026";

// Selection criteria — verbatim from the source 1-pager. Six rows.
const SELECTION_CRITERIA: Array<{
  title: string;
  description: string;
  starred?: boolean;
}> = [
  {
    title: "Utility Category Focus",
    starred: true,
    description:
      "PRIMARY criterion. Merchants in essential everyday categories (groceries, fuel, retail, fitness) are prioritised to drive habitual, high-frequency redemption behaviour among users and subscribers.",
  },
  {
    title: "Brand & Category Fit",
    description:
      "Merchant must align with everyday consumer lifestyle categories relevant to the subscriber base.",
  },
  {
    title: "Geographic Accessibility",
    description:
      "Preference for merchants with physical or digital presence accessible to a broad urban subscriber base.",
  },
  {
    title: "Redemption Volume Potential",
    description:
      "Demonstrated or projected ability to generate consistent monthly redemption activity.",
  },
  {
    title: "Customer Experience",
    description:
      "Strong service reputation; merchant must deliver a positive end-to-end redemption experience.",
  },
  {
    title: "Category Diversity",
    description:
      "Spread across multiple verticals to ensure broad appeal across different subscriber demographics.",
  },
];

export function OnePagerPage() {
  // Live per-category coin totals from coinsLedger (Jan 2026 onward window).
  const live = useLiveCategoryStats("live-spend-share-by-category");

  // Snapshot rollups from partners-current.ts.
  const rollups = useMemo(() => rollupAll(), []);

  // Effective per-category coin totals: live when live, snapshot otherwise.
  const effectivePerCategory = useMemo(() => {
    const byCode: Record<string, number> = {};
    if (live.state === "live") {
      for (const cat of PARTNER_CATEGORIES) {
        byCode[cat.code] = live.byCode[cat.code] ?? 0;
      }
    } else {
      for (const r of rollups) {
        byCode[r.category.code] = r.ytdRedemption;
      }
    }
    return byCode;
  }, [live, rollups]);

  const effectiveYtdTotal = useMemo(
    () =>
      Object.values(effectivePerCategory).reduce((s, n) => s + n, 0),
    [effectivePerCategory],
  );

  // Headline tile values: live where we have a binding, snapshot for the
  // structural counts (target list size, category count) which don't move
  // with traffic.
  const tiles = useMemo(() => {
    const merchantsTargeted = MERCHANTS.length; // 33 priority + standard
    const priorityCount = MERCHANTS.filter((m) => !m.standard).length;
    const ytd =
      live.state === "live" ? effectiveYtdTotal : HEADLINE.ytdRedemption;
    return {
      merchantsTargeted,
      ytdRedemption: ytd,
      priorityMerchants: priorityCount,
      categoryCount: PARTNER_CATEGORIES.length,
    };
  }, [live.state, effectiveYtdTotal]);

  // The 20 merchants that have any monthly redemption — same set the
  // source 1-pager renders in Section 03.
  const monthlyMerchants = useMemo<Merchant[]>(
    () =>
      MERCHANTS
        .filter((m) => (m.ytdTotal ?? 0) > 0)
        .sort((a, b) => (b.ytdTotal ?? 0) - (a.ytdTotal ?? 0)),
    [],
  );

  const monthlyTotals = useMemo(() => {
    const totals = { jan: 0, feb: 0, mar: 0, apr: 0, may: 0, ytd: 0 };
    for (const m of monthlyMerchants) {
      totals.jan += m.monthly?.jan ?? 0;
      totals.feb += m.monthly?.feb ?? 0;
      totals.mar += m.monthly?.mar ?? 0;
      totals.apr += m.monthly?.apr ?? 0;
      totals.may += m.monthly?.may ?? 0;
      totals.ytd += m.ytdTotal ?? 0;
    }
    return totals;
  }, [monthlyMerchants]);

  // Priority-share narrative: live-aware. Uses the priority-merchant YTD
  // sum as numerator when live; falls back to the briefing's frozen number
  // (₦290,286 = 73%) otherwise.
  const priorityShare = useMemo(() => {
    if (live.state === "live") {
      let pri = 0;
      for (const m of MERCHANTS) {
        if (m.standard) continue;
        // Approximation in the live path: we don't have per-merchant live
        // totals (the query is per-category), so we re-use snapshot ratio.
        pri += m.ytdTotal ?? 0;
      }
      const total = monthlyTotals.ytd;
      const pct = total > 0 ? pri / total : 0;
      return { value: pri, pct };
    }
    return {
      value: HEADLINE.priorityShareCoins,
      pct: HEADLINE.priorityShareCoins / HEADLINE.ytdRedemption,
    };
  }, [live.state, monthlyTotals.ytd]);

  // Onboarding completion %, computed from the rollups so it stays
  // consistent with the per-category table below.
  const overallCompletion = useMemo(() => {
    const onboarded = rollups.reduce((s, r) => s + r.onboardedCount, 0);
    const target = rollups.reduce(
      (s, r) => s + r.category.targetOnboarded,
      0,
    );
    return target > 0 ? onboarded / target : 0;
  }, [rollups]);

  const today = new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  async function onDownload() {
    const blob = await buildOnePagerDocx({
      generatedOn: today,
      sourceDate: SOURCE_DATE,
      tiles,
      overviewCompletion: overallCompletion,
      priorityShare,
      rollups,
      effectivePerCategory,
      monthlyMerchants,
      monthlyTotals,
      selectionCriteria: SELECTION_CRITERIA,
      liveState: live.state,
    });
    const typedBlob = new Blob([blob], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    const stamp = new Date().toISOString().slice(0, 10);
    saveAs(typedBlob, `Bvndle-Redemption-Partners-1-pager-${stamp}.docx`);
  }

  return (
    <>
      <style>{pageCss}</style>

      <div className="op-toolbar no-print">
        <div>
          <strong>Redemption Partners 1-pager · preview</strong>
          <span> · sourced {SOURCE_DATE}</span>
          {live.state === "live" && (
            <span className="op-live-chip op-live-chip-on">LIVE</span>
          )}
          {live.state === "loading" && (
            <span className="op-live-chip op-live-chip-load">LOADING</span>
          )}
          {(live.state === "fallback" || live.state === "error") && (
            <span className="op-live-chip op-live-chip-off">SNAPSHOT</span>
          )}
        </div>
        <div className="op-toolbar-right">
          <button type="button" className="op-btn" onClick={onDownload}>
            ↓ Download Word
          </button>
          <button
            type="button"
            className="op-btn op-btn-primary"
            onClick={() => window.print()}
          >
            Print → PDF
          </button>
        </div>
      </div>

      <article className="op-page">
        {/* ---- HEADER ---- */}
        <header className="op-head">
          <div className="op-eyebrow">Bvndle · ManCo</div>
          <h1 className="op-title">Redemption Partners Report</h1>
          <div className="op-meta">
            <span>{today}</span>
            <span>·</span>
            <span>Snapshot {SOURCE_DATE}</span>
            <span>·</span>
            <span>1 of 1</span>
          </div>
        </header>

        {/* ---- HEADLINE TILES ---- */}
        <section className="op-tiles">
          <Tile big={String(tiles.merchantsTargeted)} label="Merchants Targeted" />
          <Tile
            big={fmtCoin(tiles.ytdRedemption)}
            label="Coins Redeemed (YTD)"
            note={live.state === "live" ? "LIVE" : `Snapshot ${SOURCE_DATE}`}
          />
          <Tile big={String(tiles.priorityMerchants)} label="Priority Merchants" />
          <Tile big={String(tiles.categoryCount)} label="Merchant Categories" />
        </section>

        {/* ---- OVERVIEW ---- */}
        <section className="op-section">
          <h2 className="op-section-title">Overview</h2>
          <p>
            While overall onboarding has reached{" "}
            <strong>{(overallCompletion * 100).toFixed(0)}%</strong> of target
            ({rollups.reduce((s, r) => s + r.onboardedCount, 0)} of{" "}
            {rollups.reduce((s, r) => s + r.category.targetOnboarded, 0)}{" "}
            slots), the programme has secured coverage in core spending
            categories. The immediate priority is completing merchant
            acquisition in underrepresented lifestyle and experiential
            categories to achieve a balanced and commercially compelling
            ecosystem.
          </p>
          <p>
            Priority merchants contributed{" "}
            <strong>{(priorityShare.pct * 100).toFixed(0)}%</strong> (
            {fmtCoin(priorityShare.value)}) of total coin value generated
            between January and May, underscoring their role as the primary
            drivers of ecosystem value. Their outsized contribution
            validates the strategic focus on high-impact merchant
            partnerships and highlights the opportunity to further
            accelerate growth through targeted merchant acquisition,
            activation, and retention initiatives.
          </p>
        </section>

        {/* ---- SECTION 01 · CATEGORIES ---- */}
        <section className="op-section">
          <h2 className="op-section-title">
            <span className="op-sec-num">01</span> Category of Redemption
            Merchants
          </h2>
          <p className="op-section-blurb">
            Merchants are grouped into {PARTNER_CATEGORIES.length}{" "}
            categories. The Retail (Supermarkets) category is the primary
            anchor — everyday essential spending.
          </p>
          <table className="op-table">
            <thead>
              <tr>
                <th style={{ width: "22%" }}>Category</th>
                <th>Description / Focus</th>
                <th className="op-num" style={{ width: "11%" }}>Onboarded</th>
                <th className="op-num" style={{ width: "12%" }}>Completion</th>
                <th className="op-num" style={{ width: "14%" }}>YTD (coins)</th>
              </tr>
            </thead>
            <tbody>
              {rollups.map((r) => (
                <tr key={r.category.code}>
                  <td>
                    {r.category.primary && (
                      <span className="op-primary-star" aria-label="Primary">
                        ★
                      </span>
                    )}
                    {r.category.label}
                  </td>
                  <td className="op-desc">{r.category.description}</td>
                  <td className="op-num">
                    {r.onboardedCount} / {r.category.targetOnboarded}
                  </td>
                  <td className="op-num">
                    {(r.completion * 100).toFixed(0)}%
                  </td>
                  <td className="op-num">
                    {fmtCoin(effectivePerCategory[r.category.code] ?? 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* ---- SECTION 02 · CRITERIA ---- */}
        <section className="op-section">
          <h2 className="op-section-title">
            <span className="op-sec-num">02</span> Criteria for Merchant
            Selection
          </h2>
          <table className="op-table">
            <thead>
              <tr>
                <th style={{ width: "26%" }}>Selection Criterion</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {SELECTION_CRITERIA.map((c) => (
                <tr key={c.title}>
                  <td>
                    {c.starred && (
                      <span className="op-primary-star" aria-label="Primary">
                        ★
                      </span>
                    )}
                    {c.title}
                  </td>
                  <td className="op-desc">{c.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* ---- SECTION 03 · MERCHANT MONTHLY ---- */}
        <section className="op-section">
          <h2 className="op-section-title">
            <span className="op-sec-num">03</span> Redemption Value · Month-on-Month
          </h2>
          <p className="op-section-blurb">
            Merchant Redemption Details · coins redeemed per merchant ·
            January – May 2026 · successful credit transactions only.
            {live.state === "live" && (
              <em>
                {" "}· Tiles + category YTD reflect a live Metabase pull;
                per-merchant rows below remain the {SOURCE_DATE} snapshot
                until the per-merchant query lands.
              </em>
            )}
          </p>
          <table className="op-table op-merchant-table">
            <thead>
              <tr>
                <th className="op-num" style={{ width: "4%" }}>S/N</th>
                <th>Merchant</th>
                <th className="op-num">Jan</th>
                <th className="op-num">Feb</th>
                <th className="op-num">Mar</th>
                <th className="op-num">Apr</th>
                <th className="op-num">May</th>
                <th className="op-num">Total (YTD)</th>
              </tr>
            </thead>
            <tbody>
              {monthlyMerchants.map((m, i) => (
                <tr key={m.id}>
                  <td className="op-num">{i + 1}</td>
                  <td>{m.name}</td>
                  <td className="op-num">{fmtMonth(m.monthly?.jan)}</td>
                  <td className="op-num">{fmtMonth(m.monthly?.feb)}</td>
                  <td className="op-num">{fmtMonth(m.monthly?.mar)}</td>
                  <td className="op-num">{fmtMonth(m.monthly?.apr)}</td>
                  <td className="op-num">{fmtMonth(m.monthly?.may)}</td>
                  <td className="op-num op-bold">
                    {fmtCoin(m.ytdTotal ?? 0)}
                  </td>
                </tr>
              ))}
              <tr className="op-total-row">
                <td />
                <td>GRAND TOTAL</td>
                <td className="op-num">{fmtCoin(monthlyTotals.jan)}</td>
                <td className="op-num">{fmtCoin(monthlyTotals.feb)}</td>
                <td className="op-num">{fmtCoin(monthlyTotals.mar)}</td>
                <td className="op-num">{fmtCoin(monthlyTotals.apr)}</td>
                <td className="op-num">{fmtCoin(monthlyTotals.may)}</td>
                <td className="op-num op-bold">
                  {fmtCoin(monthlyTotals.ytd)}
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <div className="op-foot">
          <span>BVNDLE COMMAND CENTRE</span>
          <span>POWERED BY VFD GROUP</span>
        </div>
      </article>
    </>
  );
}

// ---------------------------------------------------------------------------
// Sub-components + formatters
// ---------------------------------------------------------------------------

function Tile({
  big,
  label,
  note,
}: {
  big: string;
  label: string;
  note?: string;
}) {
  return (
    <div className="op-tile">
      <div className="op-tile-big">{big}</div>
      <div className="op-tile-label">{label}</div>
      {note && <div className="op-tile-note">{note}</div>}
    </div>
  );
}

function fmtCoin(n: number): string {
  return n.toLocaleString("en-GB");
}

function fmtMonth(n: number | undefined): string {
  if (n == null || n === 0) return "—";
  return n.toLocaleString("en-GB");
}

// ---------------------------------------------------------------------------
// Print CSS — A4 portrait, lifted from ChairmanReportPage and tightened
// for the table-heavy 1-pager shape.
// ---------------------------------------------------------------------------

const pageCss = `
  .op-toolbar {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 14px; margin-bottom: 18px;
    background: var(--panel-2); border: 1px solid var(--edge);
    border-radius: 4px; color: var(--ink-dim); font-size: 12px;
  }
  .op-toolbar-right { display: flex; align-items: center; gap: 8px; }
  .op-btn {
    font-family: var(--font-mono); font-size: 10px; letter-spacing: 0.12em;
    text-transform: uppercase; font-weight: 700;
    padding: 7px 12px; border-radius: 3px;
    border: 1px solid var(--edge); background: transparent;
    color: var(--ink); cursor: pointer;
  }
  .op-btn-primary {
    border-color: var(--purple); background: var(--purple); color: #fff;
  }
  .op-live-chip {
    display: inline-block; margin-left: 10px;
    font-family: var(--font-mono); font-size: 9px; letter-spacing: 0.14em;
    font-weight: 700; padding: 2px 7px; border-radius: 2px;
  }
  .op-live-chip-on   { background: #2d8a4f; color: #fff; }
  .op-live-chip-load { background: #d99c3a; color: #fff; }
  .op-live-chip-off  { background: #b3b3b3; color: #fff; }

  .op-page {
    --ink: #1a1a1a; --ink-soft: #3a3a3a; --ink-faint: #6b6b6b;
    --line: #d9d9d4; --line-soft: #ebeae5; --paper: #fafaf7;
    --accent: #534AB7;
    --primary: #b8860b;

    width: 210mm; min-height: 297mm; margin: 0 auto;
    padding: 14mm 14mm 10mm;
    background: var(--paper); color: var(--ink);
    font-family: -apple-system, "SF Pro Text", system-ui, sans-serif;
    line-height: 1.42;
    box-shadow: 0 8px 32px rgba(0,0,0,0.3);
    border-radius: 2px;
    font-size: 10px;
  }

  .op-head {
    border-bottom: 1px solid var(--ink); padding-bottom: 8px;
    margin-bottom: 10px;
  }
  .op-eyebrow {
    font-family: "SF Mono", Menlo, monospace; font-size: 9px;
    letter-spacing: 0.18em; color: var(--ink-faint); font-weight: 700;
    text-transform: uppercase; margin-bottom: 4px;
  }
  .op-title {
    font-size: 22px; font-weight: 700; color: var(--ink);
    margin: 0 0 4px; letter-spacing: -0.01em;
  }
  .op-meta {
    display: flex; gap: 6px; font-size: 9px; color: var(--ink-faint);
    font-family: "SF Mono", Menlo, monospace; letter-spacing: 0.06em;
  }

  /* ---- Tiles ---- */
  .op-tiles {
    display: grid; grid-template-columns: repeat(4, 1fr);
    gap: 6px; margin-bottom: 14px;
  }
  .op-tile {
    border: 1px solid var(--line); background: #fff;
    padding: 8px 10px; border-radius: 3px;
  }
  .op-tile-big {
    font-size: 22px; font-weight: 800; color: var(--accent);
    letter-spacing: -0.02em; line-height: 1.05; margin-bottom: 2px;
  }
  .op-tile-label {
    font-size: 9px; color: var(--ink-soft);
    font-family: "SF Mono", Menlo, monospace;
    letter-spacing: 0.08em; text-transform: uppercase; font-weight: 700;
  }
  .op-tile-note {
    margin-top: 2px;
    font-size: 7.5px; color: var(--ink-faint);
    font-family: "SF Mono", Menlo, monospace; letter-spacing: 0.10em;
  }

  /* ---- Sections ---- */
  .op-section { margin-top: 10px; }
  .op-section-title {
    font-size: 12px; font-weight: 700; color: var(--ink);
    margin: 0 0 4px; padding-bottom: 3px;
    border-bottom: 0.5px solid var(--line);
  }
  .op-sec-num {
    display: inline-block; min-width: 18px;
    color: var(--accent); margin-right: 4px;
    font-family: "SF Mono", Menlo, monospace; font-size: 11px;
  }
  .op-section-blurb {
    font-size: 9.5px; color: var(--ink-soft);
    margin: 2px 0 6px;
  }
  .op-section p {
    font-size: 10px; line-height: 1.5;
    margin: 4px 0; color: var(--ink-soft);
  }
  .op-section p strong { color: var(--ink); }

  /* ---- Tables ---- */
  .op-table {
    width: 100%; border-collapse: collapse;
    font-size: 9px; margin-top: 3px;
  }
  .op-table th {
    text-align: left; padding: 4px 6px;
    background: #f1f0eb; border-bottom: 1px solid var(--ink);
    font-family: "SF Mono", Menlo, monospace;
    font-size: 8px; letter-spacing: 0.08em;
    color: var(--ink); text-transform: uppercase; font-weight: 700;
  }
  .op-table td {
    padding: 4px 6px; border-bottom: 0.5px solid var(--line-soft);
    vertical-align: top;
  }
  .op-table .op-num { text-align: right; font-variant-numeric: tabular-nums; }
  .op-table .op-bold { font-weight: 700; color: var(--ink); }
  .op-table .op-desc { color: var(--ink-soft); }
  .op-total-row {
    background: #f1f0eb; font-weight: 700;
  }
  .op-total-row td { border-bottom: none; border-top: 1px solid var(--ink); }
  .op-primary-star { color: var(--primary); margin-right: 4px; font-weight: 700; }

  .op-foot {
    margin-top: 18px; padding-top: 6px;
    border-top: 0.5px solid var(--line);
    display: flex; justify-content: space-between;
    font-family: "SF Mono", Menlo, monospace; font-size: 8px;
    letter-spacing: 0.12em; color: var(--ink-faint); text-transform: uppercase;
  }

  @media print {
    .no-print { display: none !important; }
    .op-page { box-shadow: none; border-radius: 0; margin: 0; }
    body { background: white; }
  }
`;
